import { useState, useEffect, useRef } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { RefreshCw, Coins, Crown, BadgeCheck } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import axios from 'axios';
import MatchCard from '../components/MatchCard';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Leaderboard Component
const LeaderboardView = () => {
    const { user: currentUser } = useAuth();
    const [leaderboard, setLeaderboard] = useState([]);
    const [currentUserRank, setCurrentUserRank] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchLeaderboard();
    }, []);

    const fetchLeaderboard = async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const response = await axios.get(`${API}/leaderboard`, { headers });
            setLeaderboard(response.data.leaderboard);
            setCurrentUserRank(response.data.current_user);
        } catch (error) {
            console.error('Error fetching leaderboard:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    const top3 = leaderboard.slice(0, 3);
    const rest = leaderboard.slice(3);

    // Reorder top 3 for podium display: [2nd, 1st, 3rd]
    const podiumOrder = top3.length >= 3 ? [top3[1], top3[0], top3[2]] : top3;
    const podiumStyles = [
        { border: 'border-gray-400', bg: 'bg-gray-400/20', size: 'w-16 h-16', label: '2nd', heightClass: 'pt-6' },
        { border: 'border-yellow-400', bg: 'bg-yellow-400/20', size: 'w-20 h-20', label: '1st', heightClass: 'pt-0' },
        { border: 'border-amber-600', bg: 'bg-amber-600/20', size: 'w-16 h-16', label: '3rd', heightClass: 'pt-8' },
    ];

    return (
        <div className="pb-24">
            {/* Podium Section */}
            <div className="px-4 pt-6 pb-8">
                <div className="flex items-end justify-center gap-3">
                    {podiumOrder.map((user, idx) => {
                        if (!user) return null;
                        const style = podiumStyles[idx];
                        const isFirst = idx === 1;
                        
                        return (
                            <motion.div
                                key={user.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.1 }}
                                className={`flex flex-col items-center ${style.heightClass}`}
                                data-testid={`podium-${user.rank}`}
                            >
                                {isFirst && (
                                    <Crown className="w-6 h-6 text-yellow-400 mb-1" />
                                )}
                                <div className={`relative ${style.size} rounded-full border-4 ${style.border} ${style.bg} overflow-hidden`}>
                                    <Avatar className="w-full h-full">
                                        <AvatarImage src={user.avatar} alt={user.username} />
                                        <AvatarFallback className="bg-primary text-white text-lg">
                                            {user.username?.charAt(0).toUpperCase()}
                                        </AvatarFallback>
                                    </Avatar>
                                    {user.is_verified && (
                                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-black rounded-full flex items-center justify-center">
                                            <BadgeCheck className="w-4 h-4 text-primary fill-primary" />
                                        </div>
                                    )}
                                </div>
                                <span className={`text-white font-semibold mt-2 ${isFirst ? 'text-base' : 'text-sm'}`}>
                                    {user.username}
                                </span>
                                <div className="flex items-center gap-1 mt-1">
                                    <Coins className="w-3.5 h-3.5 text-yellow-400" />
                                    <span className="text-yellow-400 text-sm font-bold">
                                        {user.coins?.toLocaleString()}
                                    </span>
                                </div>
                                <span className={`text-xs mt-1 font-medium ${
                                    isFirst ? 'text-yellow-400' : idx === 0 ? 'text-gray-400' : 'text-amber-600'
                                }`}>
                                    #{user.rank}
                                </span>
                            </motion.div>
                        );
                    })}
                </div>
            </div>

            {/* Divider */}
            <div className="px-4">
                <div className="h-px bg-border" />
            </div>

            {/* Rest of the List (4-20) */}
            <div className="px-4 py-4 space-y-2" data-testid="leaderboard-list">
                {rest.map((user, idx) => (
                    <motion.div
                        key={user.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.03 }}
                        className="flex items-center gap-3 bg-surface border border-border rounded-xl p-3"
                        data-testid={`leaderboard-row-${user.rank}`}
                    >
                        {/* Rank */}
                        <div className="w-8 h-8 rounded-full bg-card-bg flex items-center justify-center">
                            <span className="text-white/60 font-heading font-bold text-sm">
                                {user.rank}
                            </span>
                        </div>

                        {/* Avatar */}
                        <Avatar className="w-10 h-10 border border-border">
                            <AvatarImage src={user.avatar} alt={user.username} />
                            <AvatarFallback className="bg-primary text-white">
                                {user.username?.charAt(0).toUpperCase()}
                            </AvatarFallback>
                        </Avatar>

                        {/* Username */}
                        <div className="flex-1 flex items-center gap-1">
                            <span className="text-white font-medium text-sm">{user.username}</span>
                            {user.is_verified && (
                                <BadgeCheck className="w-4 h-4 text-primary fill-primary" />
                            )}
                        </div>

                        {/* Coins */}
                        <div className="flex items-center gap-1.5 bg-yellow-500/10 px-3 py-1.5 rounded-full">
                            <Coins className="w-4 h-4 text-yellow-400" />
                            <span className="text-yellow-400 text-sm font-bold">
                                {user.coins?.toLocaleString()}
                            </span>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Current User Rank Bar (Sticky Bottom) */}
            {currentUserRank && (
                <div 
                    className="fixed bottom-20 left-0 right-0 z-50 px-4"
                    data-testid="my-rank-bar"
                >
                    <div className="max-w-md mx-auto">
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            className="flex items-center gap-3 bg-primary/20 border-2 border-primary rounded-xl p-3 backdrop-blur-lg"
                        >
                            {/* Rank */}
                            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                                <span className="text-white font-heading font-bold text-sm">
                                    {currentUserRank.rank}
                                </span>
                            </div>

                            {/* Avatar */}
                            <Avatar className="w-10 h-10 border-2 border-primary">
                                <AvatarImage src={currentUserRank.avatar} alt={currentUserRank.username} />
                                <AvatarFallback className="bg-primary text-white">
                                    {currentUserRank.username?.charAt(0).toUpperCase()}
                                </AvatarFallback>
                            </Avatar>

                            {/* Username */}
                            <div className="flex-1">
                                <span className="text-white font-semibold text-sm">You</span>
                                <span className="text-white/60 text-xs ml-2">@{currentUserRank.username}</span>
                            </div>

                            {/* Coins */}
                            <div className="flex items-center gap-1.5 bg-yellow-500/20 px-3 py-1.5 rounded-full">
                                <Coins className="w-4 h-4 text-yellow-400" />
                                <span className="text-yellow-400 text-sm font-bold">
                                    {currentUserRank.coins?.toLocaleString()}
                                </span>
                            </div>
                        </motion.div>
                    </div>
                </div>
            )}
        </div>
    );
};

const LiveScoresPage = () => {
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('all');
    const simulateInterval = useRef(null);

    const fetchMatches = async () => {
        try {
            const response = await axios.get(`${API}/matches`);
            setMatches(response.data);
        } catch (error) {
            console.error('Error fetching matches:', error);
        } finally {
            setLoading(false);
        }
    };

    const simulateScores = async () => {
        try {
            await axios.post(`${API}/matches/simulate`);
            fetchMatches();
        } catch (error) {
            console.error('Error simulating scores:', error);
        }
    };

    useEffect(() => {
        fetchMatches();

        // Auto-refresh live scores every 5 seconds
        simulateInterval.current = setInterval(() => {
            simulateScores();
        }, 5000);

        return () => {
            if (simulateInterval.current) {
                clearInterval(simulateInterval.current);
            }
        };
    }, []);

    const filterMatches = (status) => {
        if (status === 'all') return matches;
        return matches.filter(m => m.status === status);
    };

    const leagues = [...new Set(matches.map(m => m.league))];

    if (loading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col bg-black" data-testid="live-scores-page">
            {/* Header */}
            <div className="flex-shrink-0 glass border-b border-white/10">
                <div className="max-w-md mx-auto px-4 py-4">
                    <div className="flex items-center justify-between mb-4">
                        <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                            {activeTab === 'leaderboard' ? 'Leaderboard' : 'Live Scores'}
                        </h1>
                        {activeTab !== 'leaderboard' && (
                            <button
                                onClick={fetchMatches}
                                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
                                data-testid="refresh-scores-button"
                            >
                                <RefreshCw className="w-5 h-5 text-white" />
                            </button>
                        )}
                    </div>

                    <Tabs value={activeTab} onValueChange={setActiveTab}>
                        <TabsList className="w-full bg-surface border border-border grid grid-cols-5">
                            <TabsTrigger 
                                value="all" 
                                className="data-[state=active]:bg-primary data-[state=active]:text-white text-xs px-2"
                                data-testid="tab-all"
                            >
                                All
                            </TabsTrigger>
                            <TabsTrigger 
                                value="live" 
                                className="data-[state=active]:bg-primary data-[state=active]:text-white text-xs px-2"
                                data-testid="tab-live"
                            >
                                <span className="w-1.5 h-1.5 bg-live rounded-full mr-1 live-pulse" />
                                Live
                            </TabsTrigger>
                            <TabsTrigger 
                                value="finished" 
                                className="data-[state=active]:bg-primary data-[state=active]:text-white text-xs px-2"
                                data-testid="tab-finished"
                            >
                                Finished
                            </TabsTrigger>
                            <TabsTrigger 
                                value="upcoming" 
                                className="data-[state=active]:bg-primary data-[state=active]:text-white text-xs px-2"
                                data-testid="tab-upcoming"
                            >
                                Soon
                            </TabsTrigger>
                            <TabsTrigger 
                                value="leaderboard" 
                                className="data-[state=active]:bg-primary data-[state=active]:text-white text-xs px-2"
                                data-testid="tab-leaderboard"
                            >
                                <Crown className="w-3 h-3 mr-1" />
                                Top
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain">
                {activeTab === 'leaderboard' ? (
                    <LeaderboardView />
                ) : (
                    <div className="max-w-md mx-auto px-4 py-4 pb-24 space-y-6" data-testid="matches-list">
                        {leagues.map((league) => {
                            const leagueMatches = filterMatches(activeTab).filter(m => m.league === league);
                            if (leagueMatches.length === 0) return null;

                            // League icons mapping
                            const leagueIcons = {
                                "Roshn Saudi League": { icon: "🏆", color: "text-yellow-400" },
                                "Yelo League": { icon: "⭐", color: "text-blue-400" },
                                "King's Cup": { icon: "👑", color: "text-primary" }
                            };
                            const leagueInfo = leagueIcons[league] || { icon: "⚽", color: "text-white/60" };
                            const liveCount = leagueMatches.filter(m => m.status === 'live').length;

                            return (
                                <motion.div
                                    key={league}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                >
                                    {/* League Header with Icon */}
                                    <div className="flex items-center gap-2 mb-3">
                                        <span className={`text-lg ${leagueInfo.color}`}>{leagueInfo.icon}</span>
                                        <h2 className="text-white text-sm font-semibold tracking-wide">
                                            {league}
                                        </h2>
                                        {liveCount > 0 && activeTab === 'all' && (
                                            <span className="ml-auto flex items-center gap-1 text-xs text-live bg-live/10 px-2 py-0.5 rounded-full">
                                                <span className="w-1.5 h-1.5 bg-live rounded-full live-pulse" />
                                                {liveCount} Live
                                            </span>
                                        )}
                                    </div>
                                    <div className="space-y-3">
                                        {leagueMatches.map((match) => (
                                            <MatchCard 
                                                key={match.id} 
                                                match={match}
                                                onUpdate={fetchMatches}
                                            />
                                        ))}
                                    </div>
                                </motion.div>
                            );
                        })}

                        {filterMatches(activeTab).length === 0 && (
                            <div className="py-20 text-center">
                                <p className="text-white/60">No matches found</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LiveScoresPage;
